import React, { useState, useRef, useEffect } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { changePassword } from '../api';

const MIN_PASSWORD_LENGTH = 4; // keep in sync with backend main.py

const FORM_ID = 'password-change-form';

const PasswordModal = ({ onClose, onPasswordChanged }) => {
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSuccess('');

    if (!oldPass || !newPass) {
      setError('Заполните все поля');
      return;
    }
    if (newPass !== confirm) {
      setFieldErrors({ confirm: 'Новые пароли не совпадают' });
      return;
    }
    if (newPass.length < MIN_PASSWORD_LENGTH) {
      setFieldErrors({ newPass: `Пароль должен быть не менее ${MIN_PASSWORD_LENGTH} символов` });
      return;
    }

    setLoading(true);
    try {
      await changePassword(oldPass, newPass);
      setSuccess('Пароль изменён');
      closeTimerRef.current = setTimeout(() => {
        onPasswordChanged();
        onClose();
      }, 800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ошибка при смене пароля');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Смена пароля"
      icon="bi-key"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" loading={loading}>
            Сменить пароль
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-xl">
        <Input
          label="Текущий пароль"
          type="password"
          value={oldPass}
          onChange={(e) => setOldPass(e.target.value)}
          autoComplete="current-password"
        />
        <Input
          label="Новый пароль"
          type="password"
          value={newPass}
          onChange={(e) => setNewPass(e.target.value)}
          error={fieldErrors.newPass}
          hint={`Минимум ${MIN_PASSWORD_LENGTH} символа`}
          autoComplete="new-password"
        />
        <Input
          label="Подтверждение"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fieldErrors.confirm}
          autoComplete="new-password"
        />

        {error && (
          <div
            role="alert"
            className="flex items-start gap-md p-lg rounded-md bg-destructive-soft border border-destructive/30 animate-fade-in"
          >
            <i className="bi bi-exclamation-triangle text-destructive" aria-hidden="true" />
            <p className="text-body-sm text-destructive">{error}</p>
          </div>
        )}
        {success && (
          <div
            role="status"
            className="flex items-start gap-md p-lg rounded-md bg-success-soft border border-success/30 animate-fade-in"
          >
            <i className="bi bi-check-circle text-success" aria-hidden="true" />
            <p className="text-body-sm text-success">{success}</p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default PasswordModal;
